import { useMemo, useRef, type ReactNode } from 'react';
import { useStore } from '@nanostores/react';

import { useUserInfoQuery } from '@/entities/user-info';
import { CareStateTimeline } from '@/features/care-state-timeline';
import { PatientChatTab } from '@/features/patient-chat/ui/PatientChatTab';
import { PatientSnapshotTab } from '@/features/patient-snapshot';
import { usePatientWorkspaceTabs } from '@/features/patient-workspace';
// import { CARE_STATE_DEV_TAB_ID } from '@/features/care-state-dev';
import { analytics } from '@/shared';
import { PatientOverlayDock, type PatientOverlayDockTab } from '@/widgets/patient-overlay-dock';
import {
  $patientPanelSelectedTab,
  $overlayDockActiveTab,
  setPatientPanelSelectedTab,
  setOverlayDockActiveTab,
} from '@/shared/stores';

import { PatientContentOutlet } from './PatientContentOutlet';
import { PatientHeader, type PatientPanelTab } from './PatientHeader';

interface PatientPanelProps {
  /** Title/name to display in the header (e.g., patient name) */
  title?: string;
  /** Workspace user ID */
  userId: string;
  /** Optional tabbed content to render inside the overlay dock */
  overlayTabs?: PatientOverlayDockTab[];
  /** Optional content to render inside the overlay dock (legacy single-panel content) */
  overlayContent?: ReactNode;
  /** Opens the workspace-level global search dialog */
  onOpenGlobalSearch?: () => void;
  /** Optional shortcut hint displayed in the search trigger tooltip */
  globalSearchShortcutLabel?: string;
}

/**
 * PatientPanel — the left workspace widget for viewing patient records.
 *
 * Composes:
 * - PatientHeader: title, view tabs, theme toggle
 * - PatientContentOutlet: scrollable content area for active view
 * - OverlayDockHost: bottom-pinned overlay dock (resizable)
 */
export function PatientPanel({
  title,
  userId,
  overlayTabs,
  overlayContent,
  onOpenGlobalSearch,
  globalSearchShortcutLabel,
}: PatientPanelProps) {
  const userInfoQuery = useUserInfoQuery(userId);

  const patientNameValue = userInfoQuery.data?.patientInfo?.name;
  const patientName = typeof patientNameValue === 'string' ? patientNameValue.trim() : '';
  const resolvedTitle =
    title ??
    (patientName !== ''
      ? patientName
      : userInfoQuery.isLoading
        ? 'Loading patient…'
        : 'Unknown Patient');

  const { tabs: tabsFromRegistry } = usePatientWorkspaceTabs(userId);
  const tabs = useMemo<PatientPanelTab[]>(
    () => tabsFromRegistry.map(({ id, label }) => ({ id, label })),
    [tabsFromRegistry]
  );

  // Store user's tab selection in a shared store so other panels can navigate.
  const selectedTab = useStore($patientPanelSelectedTab);
  const activeTab =
    selectedTab !== null && tabs.some((t) => t.id === selectedTab)
      ? selectedTab
      : (tabs[0]?.id ?? '');
  const previousWorkspaceTabRef = useRef<string | null>(null);

  // Overlay dock tab state for programmatic navigation (e.g., from Follow Ups)
  const overlayDockSelectedTab = useStore($overlayDockActiveTab);
  const previousOverlayTabRef = useRef<string | null>(null);

  const resolvedOverlayTabs = useMemo<PatientOverlayDockTab[]>(() => {
    const snapshotTab: PatientOverlayDockTab = {
      id: 'snapshot',
      label: 'Snapshot',
      content: <PatientSnapshotTab userId={userId} />,
    };

    const timelineTab: PatientOverlayDockTab = {
      id: 'care-timeline',
      label: 'Timeline',
      content: (
        <div className="p-4">
          <CareStateTimeline />
        </div>
      ),
    };

    const chatHistoryTab: PatientOverlayDockTab = {
      id: 'chat:primary',
      label: 'Chat History',
      content: <PatientChatTab userId={userId} />,
    };

    const customTabs = overlayTabs?.filter((t) => t.id !== snapshotTab.id) ?? [];
    const legacyOverlayTab: PatientOverlayDockTab[] = overlayContent
      ? [
          {
            id: 'overlay',
            label: 'Overlay',
            content: overlayContent,
          },
        ]
      : [];

    return [snapshotTab, timelineTab, chatHistoryTab, ...customTabs, ...legacyOverlayTab];
  }, [overlayContent, overlayTabs, userId]);

  return (
    <div className="relative flex h-full flex-col">
      <div className="shrink-0">
        <PatientHeader
          title={resolvedTitle}
          activeTab={activeTab}
          tabs={tabs}
          onOpenGlobalSearch={onOpenGlobalSearch}
          globalSearchShortcutLabel={globalSearchShortcutLabel}
          onTabChange={(tabId) => {
            const fromTabId = previousWorkspaceTabRef.current ?? undefined;
            if (tabId !== previousWorkspaceTabRef.current) {
              void analytics.track({
                name: 'workspace.tab.focused',
                context: {
                  actor: 'clinician',
                  patientUserId: userId,
                },
                properties: { tabId, fromTabId },
              });
            }
            previousWorkspaceTabRef.current = tabId;
            setPatientPanelSelectedTab(tabId);
          }}
        />
      </div>

      <div className="flex-1 overflow-hidden">
        <PatientContentOutlet activeTab={activeTab} tabs={tabsFromRegistry} />
      </div>

      <PatientOverlayDock
        tabs={resolvedOverlayTabs}
        activeTabId={overlayDockSelectedTab ?? undefined}
        onActiveTabIdChange={(tabId) => {
          const fromTabId = previousOverlayTabRef.current ?? undefined;
          if (tabId !== previousOverlayTabRef.current) {
            void analytics.track({
              name: 'overlay.tab.focused',
              context: {
                actor: 'clinician',
                patientUserId: userId,
              },
              properties: { tabId, fromTabId },
            });
          }
          previousOverlayTabRef.current = tabId;
          setOverlayDockActiveTab(tabId);
        }}
      />
    </div>
  );
}
